import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { cn } from '@/lib/utils';

export interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  'aria-label'?: string;
}

export interface OtpInputHandle {
  focus: () => void;
  clear: () => void;
}

export const OtpInput = forwardRef<OtpInputHandle, OtpInputProps>(function OtpInput(
  {
    length = 6,
    value,
    onChange,
    onComplete,
    invalid,
    disabled,
    autoFocus,
    id,
    'aria-label': ariaLabel = 'Código de verificação',
  },
  ref,
) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const [active, setActive] = useState(0);

  useImperativeHandle(ref, () => ({
    focus: () => refs.current[0]?.focus(),
    clear: () => {
      onChange('');
      refs.current[0]?.focus();
    },
  }));

  const chars = Array.from({ length }, (_, i) => value[i] ?? '');

  const write = useCallback(
    (next: string) => {
      const clean = next.replace(/\D/g, '').slice(0, length);
      onChange(clean);
      if (clean.length === length) onComplete?.(clean);
    },
    [length, onChange, onComplete],
  );

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const arr = chars.slice();
    arr[index] = digit;
    // fill forward if pasted chunk
    if (raw.length > 1) {
      write(raw);
      const jumpTo = Math.min(raw.replace(/\D/g, '').length, length - 1);
      refs.current[jumpTo]?.focus();
      return;
    }
    const nextValue = arr.join('');
    onChange(nextValue);
    if (digit && index < length - 1) refs.current[index + 1]?.focus();
    if (nextValue.length === length && !nextValue.includes('')) onComplete?.(nextValue);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !chars[index] && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    write(text);
    const filled = Math.min(text.replace(/\D/g, '').length, length - 1);
    refs.current[filled]?.focus();
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      id={id}
      className="flex gap-2"
    >
      {chars.map((char, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={char}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          aria-label={`Dígito ${i + 1}`}
          onFocus={() => setActive(i)}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className={cn(
            'h-12 w-10 rounded-md border bg-(--color-surface) text-center text-lg font-semibold tabular-nums text-(--color-fg) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent) disabled:opacity-50',
            invalid ? 'border-(--color-error)' : 'border-(--color-border)',
            active === i ? 'ring-1 ring-(--color-accent)' : '',
          )}
        />
      ))}
    </div>
  );
});
